import { AxiosResponse } from "axios";

import request from "../libs/request";

import { Coordinates, Reason } from "../types/location";
import { StoreId } from "../types/talk";
import { BuildingId } from "../types/building";

import {
  ImageFileInfoEachSize,
  Pageable,
  PageableResponse,
} from "./talkServices";

import { EquipmentStatus, VentilationStatus } from "./hotColdVoteServices";

const baseUrl = "building";
const ismsBaseUrl = "isms/building";

export type NearBuildingItem = BuildingId & {
  atgBuildingId: string;
  atgBuildingVocId: string;
  buildingName: string;
  address: string;
  distance: number;
  lat: number;
  lng: number;
};

export type NearBuildingServiceType =
  | "BUILDING_BBS"
  | "CAFETERIA"
  | "SPACE"
  | "VOC"
  | "REQUEST"
  | "RENTAL";

export type NearBuildingRequest = {
  buildingName?: string;
  address?: string;
  lat?: number;
  lng?: number;
  page?: number;
  pageSize?: number;
  serviceType?: NearBuildingServiceType;
  reason: Reason
};

export type NearBuildingResponse = {
  pageable: Pageable;
  items: NearBuildingItem[];
};

export const getNearBuilding = (params: NearBuildingRequest) =>
  request.get<NearBuildingResponse>(`${ismsBaseUrl}/list/near`, { params });

export const getNearVocBuilding = (params: NearBuildingRequest) =>
  request.get<NearBuildingResponse>(`${baseUrl}/voc/priority-nearby`, { params });


export type BuildingFloorItem = {
  buildingFloorId: string;
  sortOrder: number;
  floorName: string;
};

export const getBuildingFloorList = (params: BuildingId) =>
  request.get<BuildingFloorItem[]>(
    `${baseUrl}/${params.buildingId}/floor/list`
  );

export type BuildingSpaceRequest = BuildingId & {
  buildingFloorId: string;
};

export type BuildingSpaceItem = {
  buildingSpaceId: string;
  sortOrder: number;
  spaceName: string;
};

export const getBuildingSpaceList = (params: BuildingSpaceRequest) =>
  request.get<BuildingSpaceItem[]>(
    `/building/${params.buildingId}/floor/${params.buildingFloorId}/space`
  );

export type BuildingSpaceDetail = BuildingId & {
  buildingSpaceId: string;
  spaceName: string;
  buildingFloorId: string;
  sortOrder: number;
  isActive: boolean;
  buildingName: string;
  floorName: string;
  createdAt: string;
  modifiedAt: string;
  temperature: string | null;
  humidity: string | null;
  planFile: {
    items: ImageFileInfoEachSize[];
  };
  equipmentStatus: EquipmentStatus;
  ventilationStatus: VentilationStatus;
};

export type BuildingSpaceIdRequest = {
  buildingSpaceId: string;
};

export const getBuildingSpaceDetail = (params: BuildingSpaceIdRequest) =>
  request.get<BuildingSpaceDetail>(
    `${baseUrl}/space/${params.buildingSpaceId}`
  );

export type DistanceWithBuildingRequest = Coordinates & BuildingId & {reason: Reason};

export type DistanceWithBuildingResponse = {
  result: boolean;
};

export const getDistanceWithBuilding = (
  params: DistanceWithBuildingRequest
): Promise<AxiosResponse<DistanceWithBuildingResponse>> => {
  return request.get<DistanceWithBuildingResponse>(`${ismsBaseUrl}/check-near`, {
    params,
  });
};

// 2025-03-28 famous 추가 불편신고 위치 요청 
export const getDistanceWithVocBuilding = (
  lat: number, 
  lng: number,
): Promise<AxiosResponse<DistanceWithBuildingResponse>> => {
  return request.get<DistanceWithBuildingResponse>(`${baseUrl}/voc/priority-nearby`, {
    params: {
      lat,
      lng,
    },
  });
};

export type BuildingInfoResponse = {
  buildingName: string;
  address: string;
  helpDeskTel: string;
};

export const getBuildingInfo = (params: BuildingId) =>
  request.get<BuildingInfoResponse>(`${baseUrl}/${params.buildingId}`);

export type CompanyItem = BuildingId & {
  companyId: string;
  companyName: string;
  mailDomain: string;
  occupiedBuildingCount: number;
  buildingName: string;
  createdAt: string;
};

export const getBuildingCompanyList = (params: BuildingId) =>
  request.get<PageableResponse<CompanyItem>>(`${baseUrl}/company/list`, {
    params: { ...params, pageSize: 100 },
  });

export type SortType = "asc" | "desc";

export type GetBuildingStoreListRequest = BuildingId & {
  sort: SortType;
};

export type StoreItem = {
  location: string;
  name: string;
  storeId: StoreId;
  subTel: string;
  tel: string;
};

export type StoreFloorItem = {
  floorName: string;
  storeList: StoreItem[] | null;
};

export type BuildingStoreInfo = {
  buildingInfo: {
    name: string;
    address: string;
    image: string;
  };
  floorInfo: StoreFloorItem[];
};

export const getBuildingStoreList = (params: GetBuildingStoreListRequest) =>
  request.get<BuildingStoreInfo>(
    `/store/floor/${params.buildingId}/${params.sort}`
  );
